import type { File } from '@wix/ambassador-velo-apps-v1-app/types';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { extractHttpError } from '@wix/cli-http-client';

export function catchFilesMaxSizeError(err: unknown, files: File[] = []) {
  if (!files.length) {
    return;
  }

  const httpError = extractHttpError(err);

  if (!httpError) {
    return;
  }

  const filesMaxSizeRawViolations =
    httpError.response?.data?.details?.validationError?.fieldViolations?.filter(
      (v: { violatedRule: string }) => v.violatedRule === 'MAX_LENGTH'
    );

  if (!filesMaxSizeRawViolations?.length) {
    return;
  }

  const maxSizeViolationsData = tryExtractMaxSizeViolationMetadata(
    filesMaxSizeRawViolations
  );

  throw new CliError({
    code: CliErrorCode.GridAppFilesMaxLengthExceeded({
      maxSizeViolationsData,
      filePaths: files.map((f) => f.path ?? '[unknown file path]'),
    }),
    cause: httpError,
  });
}

function tryExtractMaxSizeViolationMetadata(
  filesMaxSizeViolations: Array<{ field: string; description: string }>
) {
  const fileIndexRegex = /\[(?<fileIndex>\d+)\].content/;
  const expectedAndReceivedSizeRegex =
    /has size (?<receivedSize>\d+), expected (?<expectedSize>\d+) or less/;
  const violationsData: {
    expectedSize?: number;
    files: Array<{ fileIndex: number; receivedSize: number }>;
  } = {
    expectedSize: undefined,
    files: [],
  };

  for (const filesMaxSizeViolation of filesMaxSizeViolations) {
    const fileIndexMatchResult = fileIndexRegex.exec(
      filesMaxSizeViolation.field
    );
    const expectedAndReceivedSizeMatchResult =
      expectedAndReceivedSizeRegex.exec(filesMaxSizeViolation.description);

    if (
      !(
        fileIndexMatchResult?.groups?.fileIndex &&
        expectedAndReceivedSizeMatchResult?.groups?.receivedSize &&
        expectedAndReceivedSizeMatchResult.groups.expectedSize
      )
    ) {
      return null;
    }

    violationsData.expectedSize ??= Math.trunc(
      parseInt(expectedAndReceivedSizeMatchResult.groups.expectedSize, 10) /
        1000
    );

    violationsData.files.push({
      receivedSize: Math.trunc(
        parseInt(expectedAndReceivedSizeMatchResult.groups.receivedSize, 10) /
          1000
      ),
      fileIndex: parseInt(fileIndexMatchResult.groups.fileIndex, 10),
    });
  }

  return violationsData;
}
